import { getApiUrl } from "../config/api";

/**
 * Get user settings including material radius settings
 * @param {number} userId - User ID
 * @returns {Promise<Object>} User settings object
 */
export async function getUserSettings(userId) {
  const response = await fetch(getApiUrl(`/api/users/settings/${userId}`), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      // User settings don't exist yet, return default structure
      return {
        userId,
        materialRadiusSettings: {},
        defaultRadius: 50,
      };
    }
    throw new Error(`Failed to fetch user settings: ${response.status}`);
  }

  return await response.json();
}

/**
 * Update radius for a specific material category
 * @param {number} userId - User ID
 * @param {string} materialCategory - Material category name (e.g., "STEEL", "CONCRETE")
 * @param {number} radius - Radius value in miles
 * @returns {Promise<Object>} Updated user settings
 */
export async function updateMaterialRadius(userId, materialCategory, radius) {
  const response = await fetch(
    getApiUrl(`/api/users/settings/${userId}/radius/${materialCategory}`),
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ radius }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update material radius: ${response.status}`);
  }

  return await response.json();
}

/**
 * Update multiple material radii at once
 * @param {number} userId - User ID
 * @param {Object} radiusSettings - Object with material categories as keys and radius values
 * @returns {Promise<Object>} Updated user settings
 */
export async function updateMultipleMaterialRadii(userId, radiusSettings) {
  const response = await fetch(
    getApiUrl(`/api/users/settings/${userId}/radius`),
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(radiusSettings),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update material radii: ${response.status}`);
  }

  return await response.json();
}

/**
 * Update default radius for all materials
 * @param {number} userId - User ID
 * @param {number} radius - Default radius value in miles
 * @returns {Promise<Object>} Updated user settings
 */
export async function updateDefaultRadius(userId, radius) {
  const response = await fetch(
    getApiUrl(`/api/users/settings/${userId}/default-radius`),
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ radius }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update default radius: ${response.status}`);
  }

  return await response.json();
}

/**
 * Get radius for a specific material category
 * @param {number} userId - User ID
 * @param {string} materialCategory - Material category name
 * @returns {Promise<Object>} Object with radius and materialCategory
 */
export async function getMaterialRadius(userId, materialCategory) {
  const response = await fetch(
    getApiUrl(`/api/users/settings/${userId}/radius/${materialCategory}`),
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch material radius: ${response.status}`);
  }

  return await response.json();
}

/**
 * Find dealers near client location
 * @param {number} clientId - Client ID
 * @param {string} materialCategory - Optional material category filter
 * @returns {Promise<Object>} Dealers and search criteria
 */
export async function findDealersNearby(clientId, materialCategory = null) {
  let url = getApiUrl(`/api/dealers/location/nearby?clientId=${clientId}`);
  if (materialCategory) {
    url += `&materialCategory=${materialCategory}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to find nearby dealers: ${response.status}`);
  }

  return await response.json();
}

/**
 * Find dealers by coordinates
 * @param {number} latitude - Center latitude
 * @param {number} longitude - Center longitude
 * @param {number} radius - Search radius in miles
 * @param {string} materialCategory - Optional material category filter
 * @returns {Promise<Object>} Dealers and search criteria
 */
export async function findDealersByCoordinates(latitude, longitude, radius, materialCategory = null) {
  let url = getApiUrl(
    `/api/dealers/location/search?latitude=${latitude}&longitude=${longitude}&radius=${radius}`
  );
  if (materialCategory) {
    url += `&materialCategory=${materialCategory}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to find dealers by coordinates: ${response.status}`);
  }

  return await response.json();
}

/**
 * Calculate distance between client and dealer
 * @param {number} clientId - Client ID
 * @param {number} dealerId - Dealer ID
 * @returns {Promise<number>} Distance in miles
 */
export async function calculateDistance(clientId, dealerId) {
  const response = await fetch(
    getApiUrl(`/api/dealers/location/distance?clientId=${clientId}&dealerId=${dealerId}`),
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to calculate distance: ${response.status}`);
  }

  return await response.json();
}
